/**
 * WeekView — multiple day columns (5-day or 7-day), in either density.
 * Each column carries a per-day summary header (weekday, date, event count).
 *   compact: each column is a vertical list of its events.
 *   full:    a shared hour timeline with one positioned column per day.
 * Only the days passed in (already clamped to the data range) are rendered.
 */

import { cn } from '@/lib/cn';
import { EventCard } from './EventCard';
import {
  TimelineGutter,
  TimelineGutterHeader,
  TimelineDayColumn,
  NowIndicator,
  gutterWidth,
} from './Timeline';
import {
  eventsForDay,
  buildGutterColumns,
  formatWeekday,
  formatDayNum,
  isSameDay,
  type TimeBounds,
} from './calendar.helpers';
import type { NormalizedEvent, CalendarDensity, CalendarDisplay } from './calendar.types';

function DayHeaderCell({ day, count, now }: { day: Date; count: number; now: Date }) {
  const isToday = isSameDay(day, now);
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-2 text-center">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {formatWeekday(day)}
      </span>
      <span
        className={cn(
          'flex size-6 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
          isToday ? 'bg-status-error-fg text-white' : 'text-foreground',
        )}
      >
        {formatDayNum(day)}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {count === 0 ? '—' : `${count} ${count === 1 ? 'event' : 'events'}`}
      </span>
    </div>
  );
}

export interface WeekViewProps {
  events: NormalizedEvent[];
  days: Date[];
  density: CalendarDensity;
  bounds: TimeBounds;
  now: Date;
  display: CalendarDisplay;
}

export function WeekView({ events, days, density, bounds, now, display }: WeekViewProps) {
  const perDay = days.map((d) => ({ day: d, events: eventsForDay(events, d) }));
  const columns = buildGutterColumns(days[0] ?? now, display.timezones);
  const gutterW = gutterWidth(columns);
  const minColWidth = density === 'full' ? 9 : 11; // rem; keeps columns readable
  const minWidth = `calc(${days.length * minColWidth}rem${density === 'full' ? ` + ${gutterW}px` : ''})`;

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth }}>
        {/* Header row of per-day summaries */}
        <div className="flex border-b border-border">
          {density === 'full' && <TimelineGutterHeader columns={columns} />}
          {perDay.map(({ day, events: de }, i) => (
            <div
              key={day.toISOString()}
              className={cn('flex min-w-0 flex-1', i > 0 && 'border-l border-border')}
            >
              <DayHeaderCell day={day} count={de.length} now={now} />
            </div>
          ))}
        </div>

        {/* Body */}
        {density === 'compact' ? (
          <div className="flex">
            {perDay.map(({ day, events: de }, i) => (
              <div
                key={day.toISOString()}
                className={cn(
                  'flex min-w-0 flex-1 flex-col gap-2 p-2',
                  i > 0 && 'border-l border-border',
                )}
              >
                {de.length === 0 ? (
                  <p className="px-1 py-4 text-center text-xs text-muted-foreground/60">No events</p>
                ) : (
                  de.map((e) => <EventCard key={e.id} event={e} compact hour12={display.hour12} />)
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* All-day strip (timeline columns only carry timed events) */}
            {perDay.some(({ events: de }) => de.some((e) => e.allDay)) && (
              <div className="flex border-b border-border">
                <div
                  className="flex shrink-0 items-center justify-end pr-2 text-[11px] text-muted-foreground"
                  style={{ width: gutterW }}
                >
                  all-day
                </div>
                {perDay.map(({ day, events: de }, i) => {
                  const allDay = de.filter((e) => e.allDay);
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        'flex min-w-0 flex-1 flex-col gap-1 p-1',
                        i > 0 && 'border-l border-border',
                      )}
                    >
                      {allDay.map((e) => (
                        <EventCard key={e.id} event={e} compact hour12={display.hour12} />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="py-2">
              <div className="relative flex">
                <TimelineGutter columns={columns} day={days[0] ?? now} bounds={bounds} hour12={display.hour12} />
                {perDay.map(({ day, events: de }, i) => (
                  <TimelineDayColumn
                    key={day.toISOString()}
                    day={day}
                    events={de.filter((e) => !e.allDay)}
                    bounds={bounds}
                    hour12={display.hour12}
                    className={cn(i > 0 && 'border-l border-border')}
                  />
                ))}
                {perDay.some(({ day }) => isSameDay(day, now)) && (
                  <NowIndicator bounds={bounds} now={now} gutterW={gutterW} hour12={display.hour12} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
